import React, { useState, useEffect } from 'react';
import './bixy-schools.css';

const BIXY_API_BASE =
  process.env.REACT_APP_BIXY_API_URL || 'https://bixy-backend.jollybay-27d8f37d.centralindia.azurecontainerapps.io/api/internal';
const INTERNAL_SECRET = 'bixy_internal_secret_2025';

const BixySchools = () => {
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [boardFilter, setBoardFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Custom Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null, // 'toggle_status' | 'delete'
    school: null,
    nextActive: false,
  });

  const [form, setForm] = useState({
    school_name: '',
    principal_name: '',
    principal_email: '',
    password: '',
    board: 'CBSE',
    academic_year: '2025-26',
    max_students: '',
    max_teachers: '',
    address: '',
  });

  // Auto-dismiss alert messages after 8 seconds
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchSchools = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BIXY_API_BASE}/schools`, {
        headers: { 'X-Internal-Secret': INTERNAL_SECRET },
      });
      if (!res.ok) throw new Error('Failed to fetch school list from Bixy server');
      const data = await res.json();
      setSchools(data);
      setFilteredSchools(data);
    } catch (err) {
      console.error(err);
      setError('Could not connect to Bixy AI Tutor backend.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    let result = schools;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.principal_email || '').toLowerCase().includes(q) ||
          (s.principal_name || '').toLowerCase().includes(q) ||
          (s.address || '').toLowerCase().includes(q)
      );
    }
    if (boardFilter !== 'all') {
      result = result.filter((s) => s.board === boardFilter);
    }
    if (statusFilter !== 'all') {
      const wantActive = statusFilter === 'active';
      result = result.filter((s) => (s.is_active !== false) === wantActive);
    }
    setFilteredSchools(result);
  }, [searchTerm, boardFilter, statusFilter, schools]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$';
    let pwd = '';
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm((prev) => ({ ...prev, password: pwd }));
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    try {
      const payload = {
        ...form,
        max_students: form.max_students ? parseInt(form.max_students, 10) : null,
        max_teachers: form.max_teachers ? parseInt(form.max_teachers, 10) : null,
      };

      const res = await fetch(`${BIXY_API_BASE}/schools/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Internal-Secret': INTERNAL_SECRET,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to onboard school');

      setSuccessMsg(`✅ School "${form.school_name}" onboarded successfully!`);
      setModalOpen(false);
      setForm({
        school_name: '',
        principal_name: '',
        principal_email: '',
        password: '',
        board: 'CBSE',
        academic_year: '2025-26',
        max_students: '',
        max_teachers: '',
        address: '',
      });
      fetchSchools();
    } catch (err) {
      setError(err.message);
    }
  };

  const promptToggleStatus = (school) => {
    const isCurrentlyActive = school.is_active !== false;
    const nextActive = !isCurrentlyActive;
    setConfirmModal({
      isOpen: true,
      type: 'toggle_status',
      school,
      nextActive,
    });
  };

  const executeToggleStatus = async () => {
    const { school, nextActive } = confirmModal;
    if (!school) return;

    setConfirmModal({ isOpen: false, type: null, school: null, nextActive: false });
    setTogglingId(school.id);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${BIXY_API_BASE}/schools/${school.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Internal-Secret': INTERNAL_SECRET,
        },
        body: JSON.stringify({ is_active: nextActive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update school status');

      // Update locally
      setSchools((prev) =>
        prev.map((s) => (s.id === school.id ? { ...s, is_active: nextActive } : s))
      );
      setSuccessMsg(`Status updated for "${school.name}" (${nextActive ? 'Active' : 'Inactive'}).`);
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const promptDeleteSchool = (school) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      school,
      nextActive: false,
    });
  };

  const executeDeleteSchool = async () => {
    const { school } = confirmModal;
    if (!school) return;

    setConfirmModal({ isOpen: false, type: null, school: null, nextActive: false });
    setDeletingId(school.id);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${BIXY_API_BASE}/schools/${school.id}`, {
        method: 'DELETE',
        headers: {
          'X-Internal-Secret': INTERNAL_SECRET,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to delete school');

      // Remove locally
      setSchools((prev) => prev.filter((s) => s.id !== school.id));
      setSuccessMsg(`🗑️ School "${school.name}" and all associated data deleted from database.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const copyCredentials = (school) => {
    const text = `🎓 Bixy AI Tutor Portal Credentials
School: ${school.name}
Role: School Principal / Admin
Email: ${school.principal_email}
Password: ${school.principal_password || 'Dps@2025'}`;

    navigator.clipboard.writeText(text);
    setCopiedId(school.id);
    setSuccessMsg(`📋 Credentials copied for "${school.name}".`);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setBoardFilter('all');
    setStatusFilter('all');
  };

  return (
    <div className="bixy-schools-container">
      {/* Top Header */}
      <div className="schools-header-row">
        <h2>🏫 Bixy School Partners</h2>
        <button type="button" className="onboard-btn" onClick={() => setModalOpen(true)}>
          + Onboard New School
        </button>
      </div>

      {/* Dismissible Notifications */}
      {successMsg && (
        <div className="bixy-alert success">
          <span className="alert-text">{successMsg}</span>
          <button
            type="button"
            className="alert-close-btn"
            onClick={() => setSuccessMsg(null)}
            title="Dismiss message"
          >
            ✕
          </button>
        </div>
      )}
      {error && (
        <div className="bixy-alert error">
          <span className="alert-text">⚠️ {error}</span>
          <button
            type="button"
            className="alert-close-btn"
            onClick={() => setError(null)}
            title="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="schools-filter-section">
        <div className="search-box-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by school, principal, email, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
              ✕
            </button>
          )}
        </div>

        <div className="filter-group">
          <label>Board:</label>
          <select value={boardFilter} onChange={(e) => setBoardFilter(e.target.value)}>
            <option value="all">All Boards</option>
            <option value="CBSE">CBSE</option>
            <option value="ICSE">ICSE</option>
            <option value="State Board">State Board</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {(searchTerm || boardFilter !== 'all' || statusFilter !== 'all') && (
          <button className="clear-all-filters-btn" onClick={clearAllFilters}>
            Clear All
          </button>
        )}

        <div className="total-count-badge">
          Total: <strong>{filteredSchools.length}</strong> {filteredSchools.length === 1 ? 'School' : 'Schools'}
        </div>
      </div>

      {/* Table */}
      <div className="schools-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading registered schools from Bixy system...</p>
          </div>
        ) : (
          <table className="schools-data-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.NO</th>
                <th>SCHOOL NAME</th>
                <th>PRINCIPAL EMAIL</th>
                <th>PASSWORD</th>
                <th>CAPACITY (LIMIT)</th>
                <th>BOARD</th>
                <th>ONBOARDED DATE</th>
                <th>STATUS</th>
                <th style={{ textAlign: 'center', width: '220px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchools.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data-cell">
                    {schools.length === 0
                      ? 'No schools onboarded yet. Click "+ Onboard New School" above.'
                      : 'No schools matching the applied filters.'}
                  </td>
                </tr>
              ) : (
                filteredSchools.map((school, index) => {
                  const isVisible = visiblePasswords[school.id];
                  const pwd = school.principal_password || '••••••';
                  const isActive = school.is_active !== false;
                  return (
                    <tr key={school.id}>
                      <td className="sno-cell">{index + 1}</td>
                      <td className="school-info-cell">
                        <div className="school-main-name">{school.name}</div>
                        <div className="school-sub-text">{school.address || school.id}</div>
                      </td>
                      <td className="email-cell">{school.principal_email}</td>
                      <td className="password-cell">
                        <span className="pwd-text">
                          {isVisible ? pwd : '••••••••'}
                        </span>
                        <button
                          type="button"
                          className="toggle-pwd-icon"
                          title={isVisible ? 'Hide Password' : 'Show Password'}
                          onClick={() => togglePasswordVisibility(school.id)}
                        >
                          {isVisible ? '🙈' : '👁️'}
                        </button>
                      </td>
                      <td className="capacity-cell">
                        <div className="capacity-stat">
                          <span className="cap-label">Students:</span>
                          <span className="cap-val"><strong>{school.students_count || 0}</strong> / {school.max_students ? school.max_students : 'No Limit'}</span>
                        </div>
                        <div className="capacity-stat">
                          <span className="cap-label">Teachers:</span>
                          <span className="cap-val"><strong>{school.teachers_count || 0}</strong> / {school.max_teachers ? school.max_teachers : 'No Limit'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="board-badge">{school.board}</span>
                      </td>
                      <td>
                        {school.created_at
                          ? new Date(school.created_at).toLocaleDateString(undefined, {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                          : '-'}
                      </td>
                      <td>
                        <span className={`status-pill ${isActive ? 'active' : 'inactive'}`}>
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons-group">
                          {/* Copy Creds Button */}
                          <button
                            type="button"
                            className="action-btn view-btn"
                            title="Copy Portal Login Credentials"
                            onClick={() => copyCredentials(school)}
                          >
                            {copiedId === school.id ? '✅ Copied' : '📋 Copy Creds'}
                          </button>

                          {/* Pause / Play Status Action Button */}
                          <button
                            type="button"
                            className={`action-btn icon-btn ${isActive ? 'pause-btn' : 'play-btn'}`}
                            title={isActive ? 'Deactivate School' : 'Activate School'}
                            onClick={() => promptToggleStatus(school)}
                            disabled={togglingId === school.id}
                          >
                            {togglingId === school.id ? '⏳' : isActive ? '⏸' : '▶'}
                          </button>

                          {/* Delete Button */}
                          <button
                            type="button"
                            className="action-btn icon-btn delete-btn"
                            title="Delete School and Database Records"
                            onClick={() => promptDeleteSchool(school)}
                            disabled={deletingId === school.id}
                          >
                            {deletingId === school.id ? '…' : '—'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation Modal (Replaces browser window.confirm popup) */}
      {confirmModal.isOpen && (
        <div className="bixy-modal-backdrop" onClick={() => setConfirmModal({ isOpen: false, type: null, school: null, nextActive: false })}>
          <div className="bixy-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon-badge ${confirmModal.type === 'delete' ? 'danger' : confirmModal.nextActive ? 'success' : 'warning'}`}>
              {confirmModal.type === 'delete' ? '🗑️' : confirmModal.nextActive ? '▶' : '⏸'}
            </div>

            <div className="confirm-content">
              <h3 className="confirm-title">
                {confirmModal.type === 'delete'
                  ? 'Delete School Partner'
                  : confirmModal.nextActive
                    ? 'Activate School Partner'
                    : 'Deactivate School Partner'}
              </h3>

              <p className="confirm-message">
                {confirmModal.type === 'delete' ? (
                  <>
                    Are you sure you want to permanently delete <strong>"{confirmModal.school?.name}"</strong>?
                  </>
                ) : confirmModal.nextActive ? (
                  <>
                    Are you sure you want to activate <strong>"{confirmModal.school?.name}"</strong>?
                  </>
                ) : (
                  <>
                    Are you sure you want to deactivate <strong>"{confirmModal.school?.name}"</strong>?
                  </>
                )}
              </p>

              {confirmModal.type === 'delete' ? (
                <div className="confirm-alert-box danger-box">
                  <span className="alert-icon">⚠️</span>
                  <div>
                    <strong>Warning: Permanent Database Deletion</strong>
                    <p>All classes (Class 6 to 12), principal credentials, and linked records will be permanently removed.</p>
                  </div>
                </div>
              ) : confirmModal.nextActive ? (
                <p className="confirm-subtext">
                  Portal login access will be immediately restored for the school principal and teachers.
                </p>
              ) : (
                <p className="confirm-subtext">
                  The principal and teachers of this school will temporarily be unable to log in to the portal.
                </p>
              )}
            </div>

            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn cancel"
                onClick={() => setConfirmModal({ isOpen: false, type: null, school: null, nextActive: false })}
              >
                Cancel
              </button>
              {confirmModal.type === 'delete' ? (
                <button
                  type="button"
                  className="confirm-btn danger"
                  onClick={executeDeleteSchool}
                >
                  Yes, Delete School
                </button>
              ) : (
                <button
                  type="button"
                  className={`confirm-btn ${confirmModal.nextActive ? 'success' : 'warning'}`}
                  onClick={executeToggleStatus}
                >
                  {confirmModal.nextActive ? 'Yes, Activate' : 'Yes, Deactivate'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboard Modal */}
      {modalOpen && (
        <div className="bixy-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="bixy-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="bixy-modal-header">
              <h3>Onboard New School Partner</h3>
              <button className="close-modal-btn" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="bixy-form">
              <div className="form-group">
                <label>School Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter school name"
                  value={form.school_name}
                  onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Board</label>
                  <select
                    value={form.board}
                    onChange={(e) => setForm({ ...form, board: e.target.value })}
                  >
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="State Board">State Board</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Academic Year</label>
                  <input
                    type="text"
                    placeholder="2025-26"
                    value={form.academic_year}
                    onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
                  />
                </div>
              </div>

                            <div className="form-row">
                <div className="form-group">
                  <label>Max Students (Limit)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter any student limit (e.g. 250)"
                    value={form.max_students}
                    onChange={(e) => setForm({ ...form, max_students: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Max Teachers (Limit)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter any teacher limit (e.g. 25)"
                    value={form.max_teachers}
                    onChange={(e) => setForm({ ...form, max_teachers: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Principal Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter principal full name"
                  value={form.principal_name}
                  onChange={(e) => setForm({ ...form, principal_name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Principal Official Email *</label>
                <input
                  type="email"
                  required
                  placeholder="Enter principal official email"
                  value={form.principal_email}
                  onChange={(e) => setForm({ ...form, principal_email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <div className="label-with-action">
                  <label>Initial Password *</label>
                  <button type="button" className="gen-pwd-btn" onClick={generatePassword}>
                    Generate 🔀
                  </button>
                </div>
                <input
                  type="text"
                  required
                  placeholder="Enter or generate password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>City / Address</label>
                <input
                  type="text"
                  placeholder="Enter city or address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Create & Provision School
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BixySchools;
